import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import "./styles/Blog.css";
import { API_URL } from "../config";

function Blog() {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState(null);
  const [selectedBlog, setSelectedBlog] = useState(null);
  const [modalLoading, setModalLoading] = useState(false);
  const [modalError, setModalError] = useState(null);

  useEffect(() => {
    const fetchBlogs = async () => {
      try {
        setLoading(true);
        setError(null);
        const token = localStorage.getItem("bearerToken");

        const response = await fetch(`${API_URL}/blogs?page=${currentPage}`, {
          headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: token } : {}),
          },
        });

        if (!response.ok) {
          throw new Error("Failed to fetch blogs");
        }

        const result = await response.json();
        console.log("API Response for blogs list:", result);
        setBlogs(result.data);
        setPagination(result.meta);
      } catch (err) {
        setError(err.message);
        console.error("Error fetching blogs:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchBlogs();
  }, [currentPage]);

  const handleReadMore = (post) => {
    setModalLoading(true);
    setSelectedBlog(post);
    setModalLoading(false);
  };

  const handleCloseModal = () => {
    setSelectedBlog(null);
    setModalError(null);
  };

  const handlePageChange = (page) => {
    if (page >= 1 && page <= pagination?.last_page) {
      setCurrentPage(page);
    }
  };

  if (loading) {
    return (
      <div className="blog-container">
        <div className="loading">Loading blogs...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="blog-container">
        <div className="error-message">{error}</div>
      </div>
    );
  }

  return (
    <div className="blog-container">
      {/* Hero Section */}
      <div className="blog-hero">
        <div className="hero-background"></div>
        <div className="hero-content">
          <div className="hero-badge">
            <i className="fas fa-blog"></i>
            <span>RoamBis Blog</span>
          </div>
          <h1 className="hero-title">
            Insights & <span className="gradient-text">Updates</span>
          </h1>
          <p className="hero-subtitle">
            Stay informed with the latest trends in eSIM technology, travel tips, 
            and connectivity solutions from around the world.
          </p>
          <div className="hero-stats">
            <div className="stat-item">
              <span className="stat-number">{blogs.length}+</span>
              <span className="stat-label">Articles</span>
            </div>
            <div className="stat-item">
              <span className="stat-number">Weekly</span>
              <span className="stat-label">Updates</span>
            </div>
            <div className="stat-item">
              <span className="stat-number">Expert</span>
              <span className="stat-label">Insights</span>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="blog-main">
        {blogs.length === 0 ? (
          <div className="no-blogs">
            <div className="no-blogs-icon">
              <i className="fas fa-newspaper"></i>
            </div>
            <h3>No Articles Available</h3>
            <p>We're working on bringing you amazing content. Check back soon!</p>
          </div>
        ) : (
          <>
            <div className="blog-section-header">
              <h2>Latest Articles</h2>
              <p>Discover our most recent insights and updates</p>
            </div>
            
            <div className="blog-grid">
              {blogs.map((post, index) => (
                <article key={post.id} className={`blog-card ${index === 0 ? 'featured' : ''}`}>
                  <div className="blog-card-image">
                    <img
                      src={post.featured_image || `https://picsum.photos/600/400?random=${post.id}`}
                      alt={post.title}
                      loading="lazy"
                    />
                    <div className="image-overlay">
                      <button
                        className="read-more-overlay"
                        onClick={() => handleReadMore(post)}
                        aria-label="Read article"
                      >
                        <i className="fas fa-arrow-right"></i>
                      </button>
                    </div>
                  </div>
                  <div className="blog-card-content">
                    <div className="blog-card-meta">
                      <span className="category">
                        <i className="fas fa-tag"></i>
                        {post.category || 'Technology'}
                      </span>
                      <span className="reading-time">
                        <i className="fas fa-clock"></i>
                        {Math.ceil((post.content?.length || 500) / 1000)} min read
                      </span>
                    </div>
                    <h2>{post.title}</h2>
                    <p className="excerpt">{post.description}</p>
                    <div className="blog-card-footer">
                      <div className="author-info">
                        <div className="author-avatar">
                          <i className="fas fa-user"></i>
                        </div>
                        <div className="author-details">
                          <span className="author-name">{post.author || 'RoamBis Team'}</span>
                          <span className="publish-date">
                            {new Date(post.created_at).toLocaleDateString('en-US', {
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric'
                            })}
                          </span>
                        </div>
                      </div>
                      <button
                        className="read-more-btn"
                        onClick={() => handleReadMore(post)}
                      >
                        Read More
                        <i className="fas fa-arrow-right"></i>
                      </button>
                    </div>
                  </div>
                </article>
              ))}
            </div>

          {pagination && pagination.last_page > 1 && (
            <div className="pagination">
              <button
                className="pagination-btn"
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
              >
                <i className="fas fa-chevron-left"></i> Previous
              </button>

              <div className="pagination-numbers">
                {pagination.links.map((link, index) => {
                  if (index === 0 || index === pagination.links.length - 1)
                    return null;

                  return (
                    <button
                      key={index}
                      className={`pagination-number ${
                        link.active ? "active" : ""
                      }`}
                      onClick={() => handlePageChange(parseInt(link.label))}
                      disabled={link.active}
                    >
                      {link.label}
                    </button>
                  );
                })}
              </div>

              <button
                className="pagination-btn"
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === pagination.last_page}
              >
                Next <i className="fas fa-chevron-right"></i>
              </button>
            </div>
          )}
        </>
      )}
      </div>

      {/* Blog Post Modal */}
      {(selectedBlog || modalLoading) && (
        <div className="blog-modal-overlay" onClick={handleCloseModal}>
          <div className="blog-modal" onClick={(e) => e.stopPropagation()}>
            {modalLoading ? (
              <div className="modal-loading">Loading article...</div>
            ) : modalError ? (
              <div className="modal-error">
                <p>{modalError}</p>
                <button className="modal-close" onClick={handleCloseModal}>
                  Close
                </button>
              </div>
            ) : (
              selectedBlog && (
                <>
                  <button className="modal-close" onClick={handleCloseModal}>
                    <i className="fas fa-times"></i>
                  </button>
                  <div className="modal-content">
                    {/* Title */}
                    <h1 className="modal-title">{selectedBlog.title}</h1>

                    {/* Meta information */}
                    <div className="modal-meta">
                      <span className="date">
                        <i className="fas fa-calendar"></i>{" "}
                        {new Date(selectedBlog.created_at).toLocaleDateString()}
                      </span>
                    </div>

                    {/* Description - Short introduction */}
                    <div className="modal-description">
                      <p>{selectedBlog.description}</p>
                    </div>

                    {/* Featured Image */}
                    {selectedBlog.featured_image && (
                      <div className="modal-image">
                        <img
                          src={selectedBlog.featured_image}
                          alt={selectedBlog.title}
                        />
                      </div>
                    )}

                    {/* Excerpt - Preview of the content */}
                    {selectedBlog.excerpt && (
                      <div className="modal-excerpt">
                        <p>
                          <em>{selectedBlog.excerpt}</em>
                        </p>
                      </div>
                    )}

                    {/* Main Content */}
                    <div className="modal-body">
                      <div
                        dangerouslySetInnerHTML={{
                          __html: selectedBlog.content,
                        }}
                      />
                    </div>
                  </div>
                </>
              )
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default Blog;
